import React, { useState, useEffect } from 'react';
import { Modal, Alert, Button, Typography, Space, Divider, Descriptions, Tag, Spin, Card } from 'antd';
import { 
  DollarOutlined, 
  CreditCardOutlined, 
  BuildOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { FacilityBillingService, BillingImpactPreview } from '../services/facilityBillingService';

const { Text, Paragraph } = Typography;

interface BillingImpactModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  organizationId: string;
  currentFacilityCount: number;
  newFacilityCount: number;
  actionType: 'add' | 'remove' | 'status_change';
  actionDescription: string;
  confirmLoading?: boolean;
}

export const BillingImpactModal: React.FC<BillingImpactModalProps> = ({
  visible,
  onCancel,
  onConfirm,
  organizationId,
  currentFacilityCount,
  newFacilityCount,
  actionType,
  actionDescription,
  confirmLoading = false
}) => {
  const [loading, setLoading] = useState(false);
  const [billingPreview, setBillingPreview] = useState<BillingImpactPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (visible && currentFacilityCount !== newFacilityCount) {
      fetchBillingPreview();
    }
  }, [visible, currentFacilityCount, newFacilityCount, organizationId]);

  const fetchBillingPreview = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      
      const preview = await FacilityBillingService.previewBillingImpact(
        organizationId,
        newFacilityCount,
        token
      );
      
      setBillingPreview(preview);
    } catch (error: any) {
      console.error('Error fetching billing preview:', error);
      setError(error.response?.data?.detail || 'Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    onConfirm();
  };

  const renderBillingDetails = () => {
    if (!billingPreview) return null;

    const change = billingPreview.change;
    const isAdding = change > 0;
    const isRemoving = change < 0;
    const hasCharges = billingPreview.has_subscription && billingPreview.total_immediate_charge_dollars && billingPreview.total_immediate_charge_dollars > 0;

    // Get pricing from backend response (Stripe as source of truth)
    const pricingInfo = billingPreview.pricing_info || {};
    const monthlyPricePerFacility = pricingInfo.monthly?.unit_amount_dollars || 0;
    const annualPricePerFacility = pricingInfo.annual?.unit_amount_dollars || 0;
    const setupFeePerFacility = pricingInfo.setup_fee?.unit_amount_dollars || 0;

    // Determine billing cycle and pricing to display
    const billingCycle = billingPreview.billing_cycle || 'Monthly';
    const isAnnual = billingCycle.toLowerCase() === 'annual';
    
    // Use appropriate pricing based on billing cycle
    const pricePerFacility = isAnnual ? annualPricePerFacility : monthlyPricePerFacility;
    const periodLabel = isAnnual ? 'year' : 'month';
    const displayLabel = isAnnual ? 'Annual' : 'Monthly';

    // Calculate subscription costs using the correct billing cycle
    const currentCost = billingPreview.current_facility_count * pricePerFacility;
    const newCost = billingPreview.new_facility_count * pricePerFacility;
    const subscriptionDifference = newCost - currentCost;

    return (
      <div>
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="Current Facilities">
            <Space>
              <BuildOutlined />
              {billingPreview.current_facility_count}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="New Facility Count">
            <Space>
              <BuildOutlined />
              {billingPreview.new_facility_count}
              {change !== 0 && (
                <Tag color={isAdding ? 'orange' : 'blue'}>
                  {change > 0 ? '+' : ''}{change}
                </Tag>
              )}
            </Space>
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        {/* Subscription Cost Impact */}
        {billingPreview.has_subscription && change !== 0 && (
          <Card size="small" title="Subscription Cost Changes" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>Current {displayLabel} Subscription:</Text>
                <Text strong>{FacilityBillingService.formatCurrencyFromDollars(currentCost)}/{periodLabel}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>New {displayLabel} Subscription:</Text>
                <Text strong>{FacilityBillingService.formatCurrencyFromDollars(newCost)}/{periodLabel}</Text>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>{displayLabel} {isAdding ? 'Increase' : 'Decrease'}:</Text>
                <Text strong style={{ color: isAdding ? '#ff4d4f' : '#52c41a' }}>
                  {isAdding ? '+' : ''}{FacilityBillingService.formatCurrencyFromDollars(subscriptionDifference)}/{periodLabel}
                </Text>
              </div>
              {change > 0 && (
                <>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    ${pricePerFacility}/{periodLabel} per facility
                  </Text>
                  {billingPreview.setup_fee_dollars && billingPreview.setup_fee_dollars > 0 && (
                    <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f6f6f6', borderRadius: '4px' }}>
                      <Text strong>Setup Fee: </Text>
                      <Text strong style={{ color: '#ff4d4f' }}>
                        {FacilityBillingService.formatCurrencyFromDollars(billingPreview.setup_fee_dollars)}
                      </Text>
                      <Text type="secondary"> (${setupFeePerFacility} × {Math.abs(change)} facilit{Math.abs(change) > 1 ? 'ies' : 'y'})</Text>
                    </div>
                  )}
                  {billingPreview.deferred_setup_fee_dollars && billingPreview.deferred_setup_fee_dollars > 0 && (
                    <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#e6f7ff', borderRadius: '4px' }}>
                      <Text strong>Setup Fee (when trial ends): </Text>
                      <Text strong style={{ color: '#1890ff' }}>
                        {FacilityBillingService.formatCurrencyFromDollars(billingPreview.deferred_setup_fee_dollars)}
                      </Text>
                      <Text type="secondary"> (${setupFeePerFacility} × {Math.abs(change)} facilit{Math.abs(change) > 1 ? 'ies' : 'y'})</Text>
                    </div>
                  )}
                </>
              )}
              {billingPreview.subscription_status === 'trialing' && (
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    💡 Subscription changes will take effect when your trial ends
                  </Text>
                </div>
              )}
            </Space>
          </Card>
        )}

        <Divider />

        {billingPreview.is_free_trial && (
          <Alert
            message="Free Trial Active"
            description="No charges during trial. Billing starts when trial expires."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {!billingPreview.has_subscription && !billingPreview.is_free_trial && (
          <Alert
            message="No Subscription Yet"
            description="You can add facilities now and subscribe later."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {billingPreview.has_subscription && !billingPreview.is_free_trial && (
          <div>
            {/* Show trial status if subscription is trialing */}
            {billingPreview.subscription_status === 'trialing' && (
              <Alert
                message="Subscription in Trial"
                description="Your subscription is currently in trial period. Changes will be applied but billing will start when your trial ends."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {isAdding && hasCharges && (
              <Alert
                message="Immediate Billing Impact"
                description={
                  <div>
                    <Paragraph style={{ marginBottom: 8 }}>
                      Adding {Math.abs(change)} facilit{Math.abs(change) > 1 ? 'ies' : 'y'} will result in immediate charges:
                    </Paragraph>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {billingPreview.setup_fee_dollars && billingPreview.setup_fee_dollars > 0 && (
                        <Text>
                          <strong>Setup Fee:</strong> {FacilityBillingService.formatCurrencyFromDollars(billingPreview.setup_fee_dollars)} 
                          (${FacilityBillingService.formatCurrencyFromDollars(billingPreview.setup_fee_dollars / Math.abs(change))} per facility)
                        </Text>
                      )}
                      {billingPreview.proration_amount_dollars && billingPreview.proration_amount_dollars > 0 && (
                        <Text>
                          <strong>Prorated Subscription:</strong> {FacilityBillingService.formatCurrencyFromDollars(billingPreview.proration_amount_dollars)}
                          {billingPreview.is_estimated && <Text type="secondary"> (estimated)</Text>}
                        </Text>
                      )}
                      <Text strong style={{ fontSize: '16px', color: '#ff4d4f' }}>
                        <DollarOutlined /> Total Charge: {FacilityBillingService.formatCurrencyFromDollars(billingPreview.total_immediate_charge_dollars || 0)}
                      </Text>
                    </Space>
                  </div>
                }
                type="warning"
                showIcon
                icon={<ExclamationCircleOutlined />}
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Show deferred charges during trial */}
            {isAdding && !hasCharges && billingPreview.deferred_setup_fee_dollars && billingPreview.deferred_setup_fee_dollars > 0 && (
              <Alert
                message="Deferred Billing During Trial"
                description={
                  <div>
                    <Paragraph style={{ marginBottom: 8 }}>
                      Adding {Math.abs(change)} facilit{Math.abs(change) > 1 ? 'ies' : 'y'} during trial:
                    </Paragraph>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Text>
                        <strong>Setup Fee (when trial ends):</strong> {FacilityBillingService.formatCurrencyFromDollars(billingPreview.deferred_setup_fee_dollars)}
                      </Text>
                      <Text type="secondary">
                        This will be charged when your trial expires along with your first regular billing cycle.
                      </Text>
                    </Space>
                  </div>
                }
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
              />
            )}

            {isRemoving && (
              <Alert
                message="Subscription Update"
                description={
                  <div>
                    <Paragraph>
                      Removing {Math.abs(change)} facilit{Math.abs(change) > 1 ? 'ies' : 'y'} will reduce your future billing.
                    </Paragraph>
                    {billingPreview.immediate_charges && billingPreview.proration_amount_dollars && billingPreview.proration_amount_dollars < 0 && (
                      <Text>
                        You may receive a credit of approximately {FacilityBillingService.formatCurrencyFromDollars(Math.abs(billingPreview.proration_amount_dollars))}.
                      </Text>
                    )}
                    {!billingPreview.immediate_charges && (
                      <Text type="secondary">
                        The billing reduction will take effect when your trial ends.
                      </Text>
                    )}
                  </div>
                }
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {change === 0 && (
              <Alert
                message="No Billing Impact"
                description="This change will not affect your subscription billing."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const getModalTitle = () => {
    if (!billingPreview) return 'Confirm Action';
    
    const change = billingPreview.change;
    if (change > 0) {
      return `Add ${change} Facilit${change > 1 ? 'ies' : 'y'}`;
    } else if (change < 0) {
      return `Remove ${Math.abs(change)} Facilit${Math.abs(change) > 1 ? 'ies' : 'y'}`;
    }
    return 'Update Facility';
  };

  const getConfirmButtonProps = () => {
    if (!billingPreview) return { type: 'primary' as const, children: 'Confirm' };
    
    const pricingInfo = billingPreview.pricing_info || {};
    const monthlyPricePerFacility = pricingInfo.monthly?.unit_amount_dollars || 0;
    const annualPricePerFacility = pricingInfo.annual?.unit_amount_dollars || 0;
    
    // Determine billing cycle and pricing
    const billingCycle = billingPreview.billing_cycle || 'Monthly';
    const isAnnual = billingCycle.toLowerCase() === 'annual';
    const pricePerFacility = isAnnual ? annualPricePerFacility : monthlyPricePerFacility;
    const periodLabel = isAnnual ? 'year' : 'month';
    
    // Calculate subscription cost change
    const change = billingPreview.change;
    const subscriptionIncrease = change > 0 ? change * pricePerFacility : 0;
    
    const hasCharges = billingPreview.has_subscription && 
                      billingPreview.immediate_charges &&
                      billingPreview.total_immediate_charge_dollars && 
                      billingPreview.total_immediate_charge_dollars > 0;
    
    const hasDeferredCharges = billingPreview.has_subscription &&
                              !billingPreview.immediate_charges &&
                              billingPreview.deferred_setup_fee_dollars &&
                              billingPreview.deferred_setup_fee_dollars > 0;
    
    if (hasCharges) {
      return {
        type: 'primary' as const,
        danger: true,
        icon: <CreditCardOutlined />,
        children: `Charge ${FacilityBillingService.formatCurrencyFromDollars(billingPreview.total_immediate_charge_dollars!)} & Confirm`
      };
    }
    
    if (hasDeferredCharges || (subscriptionIncrease > 0 && billingPreview.subscription_status === 'trialing')) {
      // Calculate total impact: subscription increase + setup fees
      const setupFee = billingPreview.deferred_setup_fee_dollars || 0;
      
      if (subscriptionIncrease > 0 && setupFee > 0) {
        // Show both subscription increase and setup fee
        return {
          type: 'primary' as const,
          children: `Confirm (+${FacilityBillingService.formatCurrencyFromDollars(subscriptionIncrease)}/${periodLabel} + ${FacilityBillingService.formatCurrencyFromDollars(setupFee)} setup)`
        };
      } else if (subscriptionIncrease > 0) {
        // Only subscription increase
        return {
          type: 'primary' as const,
          children: `Confirm (+${FacilityBillingService.formatCurrencyFromDollars(subscriptionIncrease)}/${periodLabel})`
        };
      } else if (setupFee > 0) {
        // Only setup fee
        return {
          type: 'primary' as const,
          children: `Confirm (${FacilityBillingService.formatCurrencyFromDollars(setupFee)} setup when trial ends)`
        };
      }
    }
    
    if (billingPreview.subscription_status === 'trialing') {
      return {
        type: 'primary' as const,
        children: 'Confirm (Trial)'
      };
    }
    
    return {
      type: 'primary' as const,
      children: 'Confirm'
    };
  };

  return (
    <Modal
      title={getModalTitle()}
      open={visible}
      onCancel={onCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          loading={confirmLoading}
          onClick={handleConfirm}
          {...getConfirmButtonProps()}
        >
          {getConfirmButtonProps().children}
        </Button>
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Alert
          message="Action Summary"
          description={actionDescription}
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Calculating billing impact...</Text>
          </div>
        </div>
      ) : error ? (
        <Alert
          message="Error Loading Billing Information"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={fetchBillingPreview}>
              Retry
            </Button>
          }
        />
      ) : (
        renderBillingDetails()
      )}
    </Modal>
  );
}; 